import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ASSET_VERIFIED, Splitter } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';

import { MainContentArea } from './MainContentArea';
import { LayoutSplitterResizeTrigger } from './LayoutSplitterResizeTrigger';
import { ContentDomainTreeChatLayout } from './ContentDomainTreeChatLayout';
import { RightPanel } from '../collaboration';

import { useTargetId } from '../../store/projectStore';
import { useCollaborationContext } from '../../hooks/layout/useCollaborationContext';
import { useMainChatLayout } from '../../hooks/layout/useMainChatLayout';
import { useSessionCompletionNotifier } from '../../hooks/chat/useSessionCompletionNotifier';
import { useMainContentTabsShallow } from '../../hooks/layout/useMainContentTabsShallow';
import { DiscoveryStatusProvider } from '../../app/providers/DiscoveryStatusProvider';
import { useAssetsDiscoveryRefresh } from '../../hooks/useAssetsDiscoveryRefresh';
import { useTabScopedAssetsFilters } from '../../store/tab/assetsTabScopedFilterStore';
import { useTabScopedDomainTree } from '../../store/tab/domainTreeTabScopedFilterStore';

type ContentChatLayoutProps = {
  children: ReactNode;
  showDomainTree?: boolean;
};

export function ContentChatLayout({
  children,
  showDomainTree = false,
}: ContentChatLayoutProps) {
  const { activeTab } = useMainContentTabsShallow();
  const { verified } = useTabScopedAssetsFilters();
  const { selectedNodes, setSelectedNodes } = useTabScopedDomainTree();

  const shouldShowDomainTree = useMemo(() => {
    if (!showDomainTree) {
      return false;
    }

    if (activeTab?.type === 'assets-list' && verified === ASSET_VERIFIED.NO) {
      return false;
    }

    return true;
  }, [activeTab?.type, showDomainTree, verified]);

  useEffect(() => {
    if (
      activeTab?.type === 'assets-list' &&
      verified === ASSET_VERIFIED.NO &&
      selectedNodes.length > 0
    ) {
      setSelectedNodes([]);
    }
  }, [activeTab?.type, selectedNodes.length, setSelectedNodes, verified]);

  const targetId = useTargetId();
  const collaborationContext = useCollaborationContext(activeTab, targetId);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const layout = useMainChatLayout();

  useSessionCompletionNotifier();

  useEffect(() => {
    if (layout.isCollapsed) {
      setIsHistoryOpen(false);
    }
  }, [layout.isCollapsed]);

  useAssetsDiscoveryRefresh({
    applicationId: targetId ?? '',
    enabled: !!targetId,
  });

  return (
    <DiscoveryStatusProvider applicationId={targetId || undefined}>
      <ContentChatLayoutBody
        showDomainTree={shouldShowDomainTree}
        layout={layout}
        collaborationContext={collaborationContext}
        isHistoryOpen={isHistoryOpen}
        onCopilotHistoryOpenChange={setIsHistoryOpen}
      >
        {children}
      </ContentChatLayoutBody>
    </DiscoveryStatusProvider>
  );
}

type ContentChatLayoutBodyProps = {
  children: ReactNode;
  showDomainTree: boolean;
  layout: ReturnType<typeof useMainChatLayout>;
  collaborationContext: ReturnType<typeof useCollaborationContext>;
  isHistoryOpen: boolean;
  onCopilotHistoryOpenChange: (isOpen: boolean) => void;
};

function ContentChatLayoutBody({
  children,
  showDomainTree,
  layout,
  collaborationContext,
  isHistoryOpen,
  onCopilotHistoryOpenChange,
}: ContentChatLayoutBodyProps) {
  return (
    <Splitter.Root
      ref={layout.splitterRef}
      key={layout.panelKey}
      orientation="horizontal"
      h="100%"
      flex="1"
      panels={layout.panels}
      size={layout.controlledSizes}
      onResize={layout.handleResize}
      onResizeEnd={layout.handleResizeEnd}
      borderWidth="0"
      gap={0}
    >
      <Splitter.Panel
        id="main"
        borderWidth="0"
        borderRadius="none"
        alignItems="stretch"
        justifyContent="flex-start"
      >
        {showDomainTree ? (
          <ContentDomainTreeChatLayout>{children}</ContentDomainTreeChatLayout>
        ) : (
          <MainContentArea>{children}</MainContentArea>
        )}
      </Splitter.Panel>

      {!layout.isCollapsed ? (
        <LayoutSplitterResizeTrigger id="main:chat" />
      ) : null}

      <Splitter.Panel
        id="chat"
        data-tour-id="copilot"
        borderWidth="0"
        borderLeftWidth={layout.isCollapsed ? 0 : '1px'}
        borderLeftColor="border.subtle"
        borderRadius="none"
        alignItems="stretch"
        justifyContent="flex-start"
        className={layout.collapsedClassName}
      >
        <Box
          h="full"
          w="full"
          visibility={layout.isCollapsed ? 'hidden' : 'visible'}
          pointerEvents={layout.isCollapsed ? 'none' : 'auto'}
        >
          <RightPanel
            entityType={collaborationContext.entityType}
            entityId={collaborationContext.entityId}
            entityName={collaborationContext.entityName}
            tabType={collaborationContext.tabType}
            onMinimize={layout.toggle}
            isHistoryOpen={isHistoryOpen}
            onCopilotHistoryOpenChange={onCopilotHistoryOpenChange}
          />
        </Box>
      </Splitter.Panel>
    </Splitter.Root>
  );
}
